import { useEffect, useReducer } from 'react';
import type { ValuationParams } from '../types/ValuationParams';
import { getValuationParams } from '../api/valuationParams';
import { setTenantId } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// Module-level cache + subscriber list, same shape as useAlerts (NOT the immutable
// reference-data hooks): the valuation parameters CHANGE within a session — running
// day-open rewrites them. The StatusFooter subscribes, and DayOpenPage calls
// refreshValuationParams() after a successful run so the footer updates without a
// page reload.
let cache: ValuationParams | null = null;
// /api/valuation-params is tenant-scoped, so the cache is too: it records which
// tenant the data belongs to and is dropped when the tenant changes.
let cachedForTenantId: number | null = null;
let inflight: Promise<ValuationParams> | null = null;
let lastError: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

function load(forceRefresh: boolean, tenantId: number | null): Promise<ValuationParams> {
  if (!forceRefresh && cache && cachedForTenantId === tenantId) return Promise.resolve(cache);
  if (inflight) return inflight;
  // Write the tenant id into api/client.ts module state before the request fires.
  // AuthContext also writes it from its own [user] effect, but on a HARD REFRESH
  // React's child-first effect ordering runs this hook's effect first, so apiGet
  // would go out without X-Tenant-Id and the backend answers 400 tenant_required.
  // Same remedy as useAlerts / useFunds.
  if (tenantId !== null) setTenantId(tenantId);
  inflight = getValuationParams()
    .then((data) => {
      cache = data;
      cachedForTenantId = tenantId;
      lastError = null;
      inflight = null;
      notify();
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      lastError = err instanceof Error ? err.message : String(err);
      notify();
      throw err;
    });
  return inflight;
}

// Called after a day-open from a screen the user is already on, so the tenant is
// long since established; reuse whichever one the cache was built for.
export function refreshValuationParams(): Promise<ValuationParams> {
  return load(true, cachedForTenantId);
}

export function clearValuationParamsCache(): void {
  cache = null;
  cachedForTenantId = null;
  inflight = null;
  lastError = null;
  notify();
}

interface UseValuationParamsResult {
  data: ValuationParams | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<ValuationParams>;
}

export function useValuationParams(): UseValuationParamsResult {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const { user } = useAuth();
  const tenantId = user?.currentTenant?.tenantId ?? null;

  useEffect(() => {
    listeners.add(forceUpdate);
    // Wait for the tenant before fetching, and re-fetch if it changes: a request
    // sent without one is rejected, and a cache built under another tenant would
    // show the wrong tenant's valuation day.
    if (tenantId !== null && inflight === null && (cache === null || cachedForTenantId !== tenantId)) {
      // Swallow — the error surfaces via lastError above.
      load(false, tenantId).catch(() => undefined);
    }
    return () => {
      listeners.delete(forceUpdate);
    };
  }, [tenantId]);

  return {
    data: cachedForTenantId === tenantId ? cache : null,
    loading: cache === null && inflight !== null,
    error: lastError,
    refresh: refreshValuationParams,
  };
}
